import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SupportProvider } from "@/context/SupportContext";
import SiteLayout from "@/components/SiteLayout";
import Home from "@/pages/Home";
import SearchPage from "@/pages/Search";
import ListingDetail from "@/pages/ListingDetail";
import {
  AuthCallback, ContentPage, ForgotPassword, Login, Register, ResetPassword, VerifyEmail,
} from "@/pages/AuthPages";
import NotFound from "@/pages/NotFound";
import { ErrorBoundary } from "@/components/ErrorBoundary";

/* Ağır modüller mobil ilk yüklemeyi şişirmesin: route bazlı kod bölme. */
const named = (loader, key) => lazy(() => loader().then((m) => ({ default: m[key] })));

const CreateListing = lazy(() => import("@/pages/CreateListing"));
const RoleOnboarding = lazy(() => import("@/pages/RoleOnboarding"));
const ProfileCompletion = lazy(() => import("@/pages/ProfileCompletion"));
const QuickStart = lazy(() => import("@/pages/QuickStart"));
const Regulations = lazy(() => import("@/pages/Regulations"));
const ComplianceCheck = lazy(() => import("@/pages/ComplianceCheck"));
const ShipmentCheck = lazy(() => import("@/pages/farm/ShipmentCheck"));
const AdminRegulations = lazy(() => import("@/pages/admin/AdminRegulations"));
const AdminSupportSessions = lazy(() => import("@/pages/admin/support/AdminSupportSessions"));
const SellerShowcase = lazy(() => import("@/pages/SellerShowcase"));
const DashboardLayout = lazy(() => import("@/pages/dashboard/DashboardLayout"));
const DashboardHome = named(() => import("@/pages/dashboard/DashboardLayout"), "DashboardHome");
const DP = () => import("@/pages/dashboard/DashboardPages");
const Favorites = named(DP, "Favorites");
const Messages = named(DP, "Messages");
const MyComplaints = named(DP, "MyComplaints");
const MyListings = named(DP, "MyListings");
const Offers = named(DP, "Offers");
const Profile = named(DP, "Profile");
const SavedSearches = named(DP, "SavedSearches");
const MyAuctions = lazy(() => import("@/pages/dashboard/MyAuctions"));
const MyStore = lazy(() => import("@/pages/dashboard/MyStore"));
const MyDocuments = lazy(() => import("@/pages/dashboard/MyDocuments"));
const RecordTransfers = lazy(() => import("@/pages/dashboard/RecordTransfers"));
const AdminRecordTransfers = lazy(() => import("@/pages/admin/AdminRecordTransfers"));
const EmailLayout = lazy(() => import("@/pages/admin/email/EmailLayout"));
const EmailDashboard = lazy(() => import("@/pages/admin/email/EmailDashboard"));
const EmailInbox = lazy(() => import("@/pages/admin/email/EmailInbox"));
const EmailSent = named(() => import("@/pages/admin/email/EmailPages"), "EmailSent");
const EmailDrafts = named(() => import("@/pages/admin/email/EmailPages"), "EmailDrafts");
const EmailScheduled = named(() => import("@/pages/admin/email/EmailPages"), "EmailScheduled");
const EmailSuppressions = named(() => import("@/pages/admin/email/EmailPages"), "EmailSuppressions");
const EmailSettingsPage = named(() => import("@/pages/admin/email/EmailPages"), "EmailSettings");
const EmailTemplates = named(() => import("@/pages/admin/email/EmailCampaignPages"), "EmailTemplates");
const EmailSegments = named(() => import("@/pages/admin/email/EmailCampaignPages"), "EmailSegments");
const EmailCampaigns = named(() => import("@/pages/admin/email/EmailCampaignPages"), "EmailCampaigns");
const EmailReports = named(() => import("@/pages/admin/email/EmailCampaignPages"), "EmailReports");
const EmailPreferences = lazy(() => import("@/pages/EmailPreferences"));
const Invite = lazy(() => import("@/pages/dashboard/Invite"));

const Auctions = lazy(() => import("@/pages/auctions/Auctions"));
const AuctionDetail = lazy(() => import("@/pages/auctions/AuctionDetail"));
const Stores = lazy(() => import("@/pages/stores/Stores"));
const StorePublic = named(() => import("@/pages/stores/Stores"), "StorePublic");
const Transport = lazy(() => import("@/pages/transport/Transport"));
const Cooperatives = lazy(() => import("@/pages/coop/Cooperatives"));
const Valuation = lazy(() => import("@/pages/valuation/Valuation"));
const Plans = lazy(() => import("@/pages/billing/Plans"));
const AiChat = lazy(() => import("@/pages/ai/AiChat"));
const Knowledge = lazy(() => import("@/pages/knowledge/Knowledge"));
const KnowledgeArticle = named(() => import("@/pages/knowledge/Knowledge"), "KnowledgeArticle");
const NotificationSettings = lazy(() => import("@/pages/settings/NotificationSettings"));
const AnimalCard = lazy(() => import("@/pages/farm/AnimalCard"));
const PrescriptionVerify = lazy(() => import("@/pages/PrescriptionVerify"));
const VetDirectory = lazy(() => import("@/pages/VetDirectory"));
const VetShowcase = lazy(() => import("@/pages/VetShowcase"));
const VetMedicines = lazy(() => import("@/pages/vet/VetMedicines"));
const VetVaccinationPrograms = lazy(() => import("@/pages/vet/VetVaccinationPrograms"));
const FarmVaccinationPrograms = lazy(() => import("@/pages/farm/FarmVaccinationPrograms"));
const HerdHealth = lazy(() => import("@/pages/farm/HerdHealth"));
const TransporterDirectory = lazy(() => import("@/pages/tools/TransporterDirectory"));
const TransporterJoin = lazy(() => import("@/pages/tools/TransporterJoin"));
const AdminHerdHealth = lazy(() => import("@/pages/admin/AdminHerdHealth"));
const AdminTransporterDirectory = lazy(() => import("@/pages/admin/AdminTransporterDirectory"));
const AdminVisitorAnalytics = lazy(() => import("@/pages/admin/AdminVisitorAnalytics"));
const VetInviteAccept = lazy(() => import("@/pages/VetInviteAccept"));
const Weather = lazy(() => import("@/pages/Weather"));
const FP = () => import("@/pages/forum/ForumPages");
const ForumHome = named(FP, "ForumHome");
const ForumTopic = named(FP, "ForumTopic");
const ForumNew = named(FP, "ForumNew");
const ForumSearch = named(FP, "ForumSearch");
const ForumLeaderboard = named(FP, "ForumLeaderboard");
const StaffTasks = lazy(() => import("@/pages/staff/StaffTasks"));
const A29 = () => import("@/pages/admin/AdminPhase29");
const AdminWeather = named(A29, "AdminWeather");
const AdminForum = named(A29, "AdminForum");
const AdminLanguages = named(A29, "AdminLanguages");

const FarmLayout = lazy(() => import("@/pages/farm/FarmLayout"));
const FarmPicker = lazy(() => import("@/pages/farm/FarmPicker"));
const FarmDashboard = lazy(() => import("@/pages/farm/FarmDashboard"));
const Animals = lazy(() => import("@/pages/farm/Animals"));
const AnimalBulkImport = lazy(() => import("@/pages/farm/AnimalBulkImport"));
const SacrificeOrganization = lazy(() => import("@/pages/farm/SacrificeOrganization"));
const SacrificeMarket = lazy(() => import("@/pages/SacrificeMarket"));
const SacrificeListingDetail = lazy(() => import("@/pages/SacrificeListingDetail"));
const MySacrifice = lazy(() => import("@/pages/MySacrifice"));
const SacrificeStaffOps = lazy(() => import("@/pages/SacrificeStaffOps"));
const SacrificeTrack = lazy(() => import("@/pages/SacrificeTrack"));
const SacrificeContractVerify = lazy(() => import("@/pages/SacrificeContractVerify"));
const SacrificeQueueBoard = lazy(() => import("@/pages/SacrificeQueueBoard"));
const CourierRoute = lazy(() => import("@/pages/CourierRoute"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const ProvinceSeoPage = lazy(() => import("@/pages/ProvinceSeoPage"));
const TransporterInvite = lazy(() => import("@/pages/TransporterInvite"));
const GrowthLayout = lazy(() => import("@/pages/admin/growth/GrowthLayout"));
const GrowthOverview = lazy(() => import("@/pages/admin/growth/GrowthOverview"));
const GrowthSeo = lazy(() => import("@/pages/admin/growth/GrowthSeo"));
const GrowthKeywords = lazy(() => import("@/pages/admin/growth/GrowthKeywords"));
const GrowthContent = lazy(() => import("@/pages/admin/growth/GrowthContent"));
const GrowthLanding = lazy(() => import("@/pages/admin/growth/GrowthLanding"));
const GrowthCampaigns = lazy(() => import("@/pages/admin/growth/GrowthCampaigns"));
const GrowthAudiences = lazy(() => import("@/pages/admin/growth/GrowthAudiences"));
const GrowthOutreach = lazy(() => import("@/pages/admin/growth/GrowthOutreach"));
const GrowthCreative = lazy(() => import("@/pages/admin/growth/GrowthCreative"));
const GrowthAbTests = lazy(() => import("@/pages/admin/growth/GrowthAbTests"));
const GrowthBudget = lazy(() => import("@/pages/admin/growth/GrowthBudget"));
const GrowthConversions = lazy(() => import("@/pages/admin/growth/GrowthConversions"));
const GrowthUtm = lazy(() => import("@/pages/admin/growth/GrowthUtm"));
const GrowthReports = lazy(() => import("@/pages/admin/growth/GrowthReports"));
const GrowthIntegrations = lazy(() => import("@/pages/admin/growth/GrowthIntegrations"));
const GrowthSettings = lazy(() => import("@/pages/admin/growth/GrowthSettings"));
const SacrificeDonationVerify = lazy(() => import("@/pages/SacrificeDonationVerify"));
const GuideHome = lazy(() => import("@/pages/guides/GuideHome"));
const GuideDetail = lazy(() => import("@/pages/guides/GuideDetail"));
const PetFoodCalculator = lazy(() => import("@/pages/tools/PetFoodCalculator"));
const VaccinationPlanner = lazy(() => import("@/pages/tools/VaccinationPlanner"));
const OfficialVetDirectory = lazy(() => import("@/pages/tools/VetDirectory"));
const FarmTeam = named(() => import("@/pages/farm/Animals"), "FarmTeam");
const AnimalDetail = lazy(() => import("@/pages/farm/AnimalDetail"));
const FarmHealth = lazy(() => import("@/pages/farm/Health"));
const Milking = lazy(() => import("@/pages/farm/Milking"));
const Scorecard = lazy(() => import("@/pages/farm/Scorecard"));
const FarmFeed = lazy(() => import("@/pages/farm/Feed"));
const FarmFinance = lazy(() => import("@/pages/farm/Finance"));
const FarmTasks = lazy(() => import("@/pages/farm/Tasks"));
const FarmPoultry = lazy(() => import("@/pages/farm/Poultry"));
const FarmReports = lazy(() => import("@/pages/farm/Reports"));
const RationPlanner = lazy(() => import("@/pages/farm/RationPlanner"));
const CropCalendar = lazy(() => import("@/pages/farm/CropCalendar"));
const DataStudio = lazy(() => import("@/pages/farm/DataStudio"));
const FarmConnect = lazy(() => import("@/pages/farm/Connect"));
const FarmAlerts = lazy(() => import("@/pages/farm/FarmAlerts"));
const FarmVetAccess = lazy(() => import("@/pages/farm/FarmVetAccess"));
const FarmVetRecords = lazy(() => import("@/pages/farm/FarmVetRecords"));
const FarmVetRequests = lazy(() => import("@/pages/farm/FarmVetRequests"));
const FarmVetAppointments = lazy(() => import("@/pages/farm/FarmVetAppointments"));
const FarmVetAccount = lazy(() => import("@/pages/farm/FarmVetAccount"));

const VetLayout = lazy(() => import("@/pages/vet/VetLayout"));
const VetDashboard = lazy(() => import("@/pages/vet/VetDashboard"));
const VW = () => import("@/pages/vet/VetWorkspace");
const VetToday = named(VW, "VetToday");
const VetFarms = named(VW, "VetFarms");
const VetPatients = named(VW, "VetPatients");
const VetFarmDetail = lazy(() => import("@/pages/vet/VetFarmDetail"));
const VetAnimalCard = lazy(() => import("@/pages/vet/VetAnimalCard"));
const VetProfile = lazy(() => import("@/pages/vet/VetProfile"));
const VetPrescriptions = lazy(() => import("@/pages/vet/VetPrescriptions"));
const VetFinance = lazy(() => import("@/pages/vet/VetFinance"));
const VetRequests = lazy(() => import("@/pages/vet/VetRequests"));
const VetSchedule = lazy(() => import("@/pages/vet/VetSchedule"));

const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AP = () => import("@/pages/admin/AdminPages");
const AdminAudit = named(AP, "AdminAudit");
const AdminCategories = named(AP, "AdminCategories");
const AdminComplaints = named(AP, "AdminComplaints");
const AdminContents = named(AP, "AdminContents");
const AdminDashboard = named(AP, "AdminDashboard");
const AdminNotifications = named(AP, "AdminNotifications");
const AdminSettings = named(AP, "AdminSettings");
const AdminSupport = named(AP, "AdminSupport");
const AdminUsers = named(AP, "AdminUsers");
const AdminUserCenter = lazy(() => import("@/pages/admin/AdminUserCenter"));
const AdminFarmCenter = lazy(() => import("@/pages/admin/AdminFarmCenter"));
const AdminCommandCenter = lazy(() => import("@/pages/admin/AdminCommandCenter"));
const AdminVerifications = named(AP, "AdminVerifications");
const AllListings = named(AP, "AllListings");
const Moderation = named(AP, "Moderation");
const AE = () => import("@/pages/admin/AdminEcosystem");
const AdminBilling = named(AE, "AdminBilling");
const AdminCampaigns = named(AE, "AdminCampaigns");
const AdminCooperatives = named(AE, "AdminCooperatives");
const AdminStores = named(AE, "AdminStores");
const AdminTransport = named(AE, "AdminTransport");
const AdminWhatsapp = named(AE, "AdminWhatsapp");
const A3 = () => import("@/pages/admin/AdminPhase3");
const AdminAiSafety = named(A3, "AdminAiSafety");
const AdminCropCalendars = named(A3, "AdminCropCalendars");
const AdminIntegrations = named(A3, "AdminIntegrations");
const AdminKnowledge = named(A3, "AdminKnowledge");
const AdminRations = named(A3, "AdminRations");
const AdminAuctions = lazy(() => import("@/pages/admin/AdminAuctions"));
const AdminBrand = lazy(() => import("@/pages/admin/AdminBrand"));
const AdminSystemStatus = lazy(() => import("@/pages/admin/AdminSystemStatus"));
const AdminVideoStudio = lazy(() => import("@/pages/admin/AdminVideoStudio"));
const MedicineInfo = lazy(() => import("@/pages/MedicineInfo"));
const AdminVetVerification = named(() => import("@/pages/admin/AdminVet"), "AdminVetVerification");
const AdminVetDirectory = lazy(() => import("@/pages/admin/AdminVetDirectory"));
const AdminVetMedicines = named(() => import("@/pages/admin/AdminVet"), "AdminVetMedicines");

const ButcherLayout = lazy(() => import("@/pages/butcher/ButcherLayout"));
const BP = () => import("@/pages/butcher/ButcherPages");
const ButcherDashboard = named(BP, "ButcherDashboard");
const ButcherProfilePage = named(BP, "ButcherProfile");
const ButcherRequests = named(BP, "ButcherRequests");
const ButcherQuotes = named(BP, "ButcherQuotes");
const ButcherContracts = named(BP, "ButcherContracts");
const BO = () => import("@/pages/butcher/ButcherOpsPages");
const ButcherCalendar = named(BO, "ButcherCalendar");
const ButcherCarcasses = named(BO, "ButcherCarcasses");
const ButcherInventory = named(BO, "ButcherInventory");
const ButcherColdRooms = named(BO, "ButcherColdRooms");
const ButcherFinance = named(BO, "ButcherFinance");
const ButcherReports = named(BO, "ButcherReports");
const ButcherPurchaseRequests = named(BO, "ButcherPurchaseRequests");
const ButcherDirectory = lazy(() => import("@/pages/ButcherDirectory"));
const BD = () => import("@/pages/ButcherDirectory");
const ButcherPublicDetail = named(BD, "ButcherPublicDetail");
const ContractVerify = named(BD, "ContractVerify");
const CarcassTrace = named(BD, "CarcassTrace");
const FarmSlaughter = lazy(() => import("@/pages/farm/FarmSlaughter"));
const AdminButcher = named(() => import("@/pages/admin/AdminButcher"), "AdminButcher");
const BOR = () => import("@/pages/butcher/ButcherOrdersPages");
const ButcherOrders = named(BOR, "ButcherOrders");
const ButcherMobile = named(BOR, "ButcherMobile");
const ButcherShop = lazy(() => import("@/pages/ButcherShop"));
const MarketPrices = lazy(() => import("@/pages/market/MarketPrices"));
const MarketCompare = lazy(() => import("@/pages/market/MarketCompare"));
const MarketMap = lazy(() => import("@/pages/market/MarketMap"));
const ExchangeDetail = named(() => import("@/pages/market/MarketCompare"), "ExchangeDetail");
const AdminMarketSources = lazy(() => import("@/pages/admin/AdminMarketSources"));
const AdminForumSeed = lazy(() => import("@/pages/admin/AdminForumSeed"));
const AdminFairs = lazy(() => import("@/pages/admin/AdminFairs"));
const Fairs = lazy(() => import("@/pages/Fairs"));
const FairDetail = lazy(() => import("@/pages/FairDetail"));
const AdminListingManagement = lazy(() => import("@/pages/admin/AdminListingManagement"));
const AdminDataCleanup = lazy(() => import("@/pages/admin/AdminDataCleanup"));
const AdminDemoListings = lazy(() => import("@/pages/admin/AdminDemoListings"));
const MyButcherOrders = named(() => import("@/pages/ButcherShop"), "MyButcherOrders");

const PageLoader = () => (
  <div className="px-4 py-20 text-center text-sm text-muted-foreground" data-testid="page-loading">
    Yükleniyor...
  </div>
);

/** Route değişiminde yapışık scroll kilitlerini temizler ve sayfayı başa alır. */
function RouteEffects() {
  const { pathname } = useLocation();
  useEffect(() => {
    const body = document.body;
    ["overflow", "position", "top", "width", "padding-right", "touch-action"].forEach((p) =>
      body.style.removeProperty(p),
    );
    body.removeAttribute("data-scroll-locked");
    document.documentElement.style.removeProperty("overflow");
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <RouteEffects />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/ilanlar" element={<SearchPage />} />
            <Route path="/ilan/:id" element={<ListingDetail />} />
            <Route path="/ilan-ver" element={<CreateListing />} />
            <Route path="/satici/:id" element={<SellerShowcase />} />
            <Route path="/sayfa/:slug" element={<ContentPage />} />
            <Route path="/giris" element={<Login />} />
            <Route path="/kayit" element={<Register />} />
            <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
            <Route path="/sifre-sifirla" element={<ResetPassword />} />
            <Route path="/e-posta-dogrula" element={<VerifyEmail />} />
            <Route path="/hosgeldin" element={<RoleOnboarding />} />
            <Route path="/hesap-tamamla" element={<ProfileCompletion />} />
            <Route path="/hizli-kurulum" element={<QuickStart />} />
            <Route path="/mevzuat" element={<Regulations />} />
            <Route path="/panel" element={<DashboardLayout />}>
              <Route index element={<DashboardHome />} />
              <Route path="ilanlarim" element={<MyListings />} />
              <Route path="favoriler" element={<Favorites />} />
              <Route path="aramalar" element={<SavedSearches />} />
              <Route path="mesajlar" element={<Messages />} />
              <Route path="teklifler" element={<Offers />} />
              <Route path="mezatlarim" element={<MyAuctions />} />
              <Route path="magazam" element={<MyStore />} />
              <Route path="belgeler" element={<MyDocuments />} />
              <Route path="kayit-transferleri" element={<RecordTransfers />} />
              <Route path="siparislerim" element={<MyButcherOrders />} />
              <Route path="sikayetler" element={<MyComplaints />} />
              <Route path="davet" element={<Invite />} />
              <Route path="profil" element={<Profile />} />
            </Route>
            <Route path="/ciftligim" element={<FarmPicker />} />
            <Route path="/ciftora-ai" element={<AiChat />} />
            <Route path="/veteriner-ilac/:medicineId" element={<MedicineInfo />} />
            <Route path="/mezat" element={<Auctions />} />
            <Route path="/mezat/:auctionId" element={<AuctionDetail />} />
            <Route path="/magazalar" element={<Stores />} />
            <Route path="/magaza/:slug" element={<StorePublic />} />
            <Route path="/nakliye" element={<Transport />} />
            <Route path="/kooperatif" element={<Cooperatives />} />
            <Route path="/degerleme" element={<Valuation />} />
            <Route path="/planlar" element={<Plans />} />
            <Route path="/bilgi-merkezi" element={<Knowledge />} />
            <Route path="/bilgi-merkezi/:slug" element={<KnowledgeArticle />} />
            <Route path="/bildirimler" element={<NotificationSettings />} />
            <Route path="/hayvan-kart/:token" element={<AnimalCard />} />
            <Route path="/recete-dogrula/:token" element={<PrescriptionVerify />} />
            <Route path="/hava" element={<Weather />} />
            <Route path="/forum" element={<ForumHome />} />
            <Route path="/forum/yeni" element={<ForumNew />} />
            <Route path="/forum/ara" element={<ForumSearch />} />
            <Route path="/forum/itibar" element={<ForumLeaderboard />} />
            <Route path="/forum/konu/:topicId" element={<ForumTopic />} />
            <Route path="/forum/:categorySlug/:topicSlug" element={<ForumTopic />} />
            <Route path="/gorevlerim" element={<StaffTasks />} />
            <Route path="/veteriner-bul" element={<VetDirectory />} />
            <Route path="/fiyatlar" element={<MarketPrices />} />
            <Route path="/fiyatlar-karsilastir" element={<MarketCompare />} />
            <Route path="/fiyat-haritasi" element={<MarketMap />} />
            <Route path="/fuarlar" element={<Fairs />} />
            <Route path="/kurban" element={<SacrificeMarket />} />
            <Route path="/kurban/:code" element={<SacrificeListingDetail />} />
            <Route path="/kurbanim" element={<MySacrifice />} />
            <Route path="/kurban-personel/:opsToken" element={<SacrificeStaffOps />} />
            <Route path="/kurban-takip/:trackToken" element={<SacrificeTrack />} />
            <Route path="/kurban-sozlesme/:token" element={<SacrificeContractVerify />} />
            <Route path="/kurban-sira/:farmId" element={<SacrificeQueueBoard />} />
            <Route path="/kurban-bagis/:token" element={<SacrificeDonationVerify />} />
            <Route path="/kurye/:token" element={<CourierRoute />} />
            <Route path="/k/:slug" element={<LandingPage />} />
            <Route path="/buyukbas-ilanlari/:province"
              element={<ProvinceSeoPage kind="buyukbas-ilanlari" />} />
            <Route path="/kucukbas-ilanlari/:province"
              element={<ProvinceSeoPage kind="kucukbas-ilanlari" />} />
            <Route path="/hayvan-nakliye/:province"
              element={<ProvinceSeoPage kind="hayvan-nakliye" />} />
            <Route path="/tarim-fuarlari/:province"
              element={<ProvinceSeoPage kind="tarim-fuarlari" />} />
            <Route path="/nakliyeci-davet/:province" element={<TransporterInvite />} />
            <Route path="/kilavuz" element={<GuideHome />} />
            <Route path="/kilavuz/:slug" element={<GuideDetail />} />
            <Route path="/kopek-mama-hesapla" element={<PetFoodCalculator />} />
            <Route path="/pet-mama-hesapla" element={<PetFoodCalculator />} />
            <Route path="/asi-takvimi" element={<VaccinationPlanner />} />
            <Route path="/veterinerler" element={<OfficialVetDirectory />} />
            <Route path="/nakliyeci-bul" element={<TransporterDirectory />} />
            <Route path="/nakliyeci-kayit" element={<TransporterJoin />} />
            <Route path="/nakliyeci-bul/:province" element={<TransporterDirectory />} />
            <Route path="/veterinerler/:province" element={<OfficialVetDirectory />} />
            <Route path="/veterinerler/:province/:district" element={<OfficialVetDirectory />} />
            <Route path="/fuarlar/:slug" element={<FairDetail />} />
            <Route path="/fiyatlar/:province" element={<MarketPrices />} />
            <Route path="/borsa/:exchangeId" element={<ExchangeDetail />} />
            <Route path="/kasap-bul" element={<ButcherDirectory />} />
            <Route path="/kasap-bul/:slug" element={<ButcherPublicDetail />} />
            <Route path="/kasap-bul/:slug/siparis" element={<ButcherShop />} />
            <Route path="/sozlesme-dogrula/:token" element={<ContractVerify />} />
            <Route path="/karkas/:token" element={<CarcassTrace />} />
            <Route path="/veteriner-hekim/:slug" element={<VetShowcase />} />
            <Route path="/veteriner-davet/:token" element={<VetInviteAccept />} />
            <Route path="/e-posta-tercihleri" element={<EmailPreferences />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/ciftligim/:farmId" element={<FarmLayout />}>
            <Route index element={<FarmDashboard />} />
            <Route path="hayvanlar" element={<Animals />} />
            <Route path="hayvanlar/toplu-ekle" element={<AnimalBulkImport />} />
            <Route path="hayvanlar/:animalId" element={<AnimalDetail />} />
            <Route path="kurban" element={<SacrificeOrganization />} />
            <Route path="saglik" element={<FarmHealth />} />
            <Route path="sagim" element={<Milking />} />
            <Route path="karne" element={<Scorecard />} />
            <Route path="yem" element={<FarmFeed />} />
            <Route path="finans" element={<FarmFinance />} />
            <Route path="gorevler" element={<FarmTasks />} />
            <Route path="kanatli-arici" element={<FarmPoultry />} />
            <Route path="raporlar" element={<FarmReports />} />
            <Route path="ekip" element={<FarmTeam />} />
            <Route path="veteriner-erisimi" element={<FarmVetAccess />} />            <Route path="veteriner-islemleri" element={<FarmVetRecords />} />
            <Route path="veteriner-cagri" element={<FarmVetRequests />} />
            <Route path="veteriner-randevu" element={<FarmVetAppointments />} />
            <Route path="veteriner-hesap" element={<FarmVetAccount />} />
            <Route path="asi-programi" element={<FarmVaccinationPrograms />} />
            <Route path="suru-sagligi" element={<HerdHealth />} />
            <Route path="rasyon" element={<RationPlanner />} />
            <Route path="takvim" element={<CropCalendar />} />
            <Route path="veri" element={<DataStudio />} />
            <Route path="alarmlar" element={<FarmAlerts />} />
            <Route path="baglantilar" element={<FarmConnect />} />
            <Route path="kesim-kasap" element={<FarmSlaughter />} />
            <Route path="sevk-kontrol" element={<ShipmentCheck />} />
            <Route path="sut-uyum" element={<ComplianceCheck kind="MILK" />} />          </Route>
          <Route path="/kasap" element={<ButcherLayout />}>
            <Route index element={<ButcherDashboard />} />
            <Route path="talepler" element={<ButcherRequests />} />
            <Route path="tekliflerim" element={<ButcherQuotes />} />
            <Route path="sozlesmeler" element={<ButcherContracts />} />
            <Route path="takvim" element={<ButcherCalendar />} />
            <Route path="kesim" element={<ButcherCarcasses />} />
            <Route path="stok" element={<ButcherInventory />} />
            <Route path="soguk-oda" element={<ButcherColdRooms />} />
            <Route path="finans" element={<ButcherFinance />} />
            <Route path="raporlar" element={<ButcherReports />} />
            <Route path="alim-talepleri" element={<ButcherPurchaseRequests />} />
            <Route path="siparisler" element={<ButcherOrders />} />
            <Route path="mobil" element={<ButcherMobile />} />
            <Route path="isletme" element={<ButcherProfilePage />} />
            <Route path="et-uyum" element={<ComplianceCheck kind="MEAT" />} />
          </Route>

          <Route path="/yonetim" element={<AdminLayout />}>
            <Route path="kasap" element={<AdminButcher />} />
            <Route path="fiyatlar" element={<Navigate to="/yonetim/piyasa" replace />} />
            <Route path="piyasa" element={<AdminMarketSources />} />
            <Route path="forum-baslangic" element={<AdminForumSeed />} />
            <Route path="fuarlar" element={<AdminFairs />} />
            <Route path="ilan-yonetimi" element={<AdminListingManagement />} />
            <Route path="veri-temizligi" element={<AdminDataCleanup />} />
            <Route path="ornek-ilanlar" element={<AdminDemoListings />} />            <Route index element={<AdminCommandCenter />} />
            <Route path="pano-eski" element={<AdminDashboard />} />
            <Route path="mevzuat" element={<AdminRegulations />} />
            <Route path="onaylar" element={<Moderation />} />
            <Route path="mezatlar" element={<AdminAuctions />} />
            <Route path="magazalar" element={<AdminStores />} />
            <Route path="nakliye" element={<AdminTransport />} />
            <Route path="kooperatifler" element={<AdminCooperatives />} />
            <Route path="whatsapp" element={<AdminWhatsapp />} />
            <Route path="kampanyalar" element={<AdminCampaigns />} />
            <Route path="abonelikler" element={<AdminBilling />} />
            <Route path="ilanlar" element={<AllListings />} />
            <Route path="kullanicilar" element={<AdminUserCenter />} />
            <Route path="ciftlikler" element={<AdminFarmCenter />} />
            <Route path="kullanicilar/moderasyon" element={<AdminUsers />} />
            <Route path="dogrulamalar" element={<AdminVerifications />} />
            <Route path="sikayetler" element={<AdminComplaints />} />
            <Route path="kategoriler" element={<AdminCategories />} />
            <Route path="destek" element={<AdminSupport />} />
            <Route path="icerikler" element={<AdminContents />} />
            <Route path="bildirimler" element={<AdminNotifications />} />
            <Route path="ayarlar" element={<AdminSettings />} />
            <Route path="marka" element={<AdminBrand />} />
            <Route path="sistem-durumu" element={<AdminSystemStatus />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="destek-erisimleri" element={<AdminSupportSessions />} />
            <Route path="ciftora-ai" element={<AdminAiSafety />} />
            <Route path="bilgi-merkezi" element={<AdminKnowledge />} />
            <Route path="rasyonlar" element={<AdminRations />} />
            <Route path="takvimler" element={<AdminCropCalendars />} />
            <Route path="entegrasyonlar" element={<AdminIntegrations />} />
            <Route path="veterinerler" element={<AdminVetVerification />} />
            <Route path="veteriner-dizini" element={<AdminVetDirectory />} />
            <Route path="suru-sagligi" element={<AdminHerdHealth />} />
            <Route path="nakliyeci-daveti" element={<AdminTransporterDirectory />} />
            <Route path="ziyaretciler" element={<AdminVisitorAnalytics />} />
            <Route path="buyume" element={<GrowthLayout />}>
              <Route index element={<GrowthOverview />} />
              <Route path="seo" element={<GrowthSeo />} />
              <Route path="kelimeler" element={<GrowthKeywords />} />
              <Route path="icerik" element={<GrowthContent />} />
              <Route path="sayfalar" element={<GrowthLanding />} />
              <Route path="kampanyalar" element={<GrowthCampaigns />} />
              <Route path="kitleler" element={<GrowthAudiences />} />
              <Route path="kazanim" element={<GrowthOutreach />} />
              <Route path="reklam-metni" element={<GrowthCreative />} />
              <Route path="testler" element={<GrowthAbTests />} />
              <Route path="butce" element={<GrowthBudget />} />
              <Route path="donusumler" element={<GrowthConversions />} />
              <Route path="utm" element={<GrowthUtm />} />
              <Route path="raporlar" element={<GrowthReports />} />
              <Route path="entegrasyonlar" element={<GrowthIntegrations />} />
              <Route path="ayarlar" element={<GrowthSettings />} />
            </Route>
            <Route path="ilac-katalogu" element={<AdminVetMedicines />} />
            <Route path="hava" element={<AdminWeather />} />
            <Route path="forum" element={<AdminForum />} />
            <Route path="diller" element={<AdminLanguages />} />
            <Route path="kayit-transferleri" element={<AdminRecordTransfers />} />
            <Route path="video-studyosu" element={<AdminVideoStudio />} />
            <Route path="e-posta" element={<EmailLayout />}>
              <Route index element={<EmailDashboard />} />
              <Route path="gelen" element={<EmailInbox />} />
              <Route path="gonderilenler" element={<EmailSent />} />
              <Route path="taslaklar" element={<EmailDrafts />} />
              <Route path="zamanlananlar" element={<EmailScheduled />} />
              <Route path="kampanyalar" element={<EmailCampaigns />} />
              <Route path="sablonlar" element={<EmailTemplates />} />
              <Route path="segmentler" element={<EmailSegments />} />
              <Route path="raporlar" element={<EmailReports />} />
              <Route path="engellenenler" element={<EmailSuppressions />} />
              <Route path="ayarlar" element={<EmailSettingsPage />} />
            </Route>
          </Route>
          <Route path="/veteriner" element={<VetLayout />}>
            <Route index element={<VetDashboard />} />
            <Route path="bugun" element={<VetToday />} />
            <Route path="ciftlikler" element={<VetFarms />} />
            <Route path="ciftlikler/:farmId" element={<VetFarmDetail />} />
            <Route path="ciftlikler/:farmId/receteler" element={<VetPrescriptions />} />
            <Route path="cagrilar" element={<VetRequests />} />
            <Route path="ilaclar" element={<VetMedicines />} />
            <Route path="asi-programi" element={<VetVaccinationPrograms />} />
            <Route path="takvim" element={<VetSchedule />} />
            <Route path="finans" element={<VetFinance />} />
            <Route path="ciftlikler/:farmId/hayvan/:animalId" element={<VetAnimalCard />} />
            <Route path="hasta-ara" element={<VetPatients />} />
            <Route path="profil" element={<VetProfile />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <SupportProvider>
            <AppRouter />
          </SupportProvider>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
